import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, Plus } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { apiGetPortalState, apiProjectsList } from "@/lib/api";
import { readLocalState, writeLocalState } from "@/lib/sync/localStore";
import {
    DASHBOARD_PHASES,
    getActivePhase,
    phaseCompletionPct,
    phaseStatus,
} from "@/lib/portal/phaseGrouping";
import { CURRICULUM } from "@/lib/curriculum";
import {
    accessStateLabel,
    buildPathLabel,
    displayName,
} from "@/lib/portal/buildPathLabel";

import PortalNav from "@/components/portal/PortalNav";
import PhaseCard from "@/components/portal/PhaseCard";
import PortalModuleCard from "@/components/portal/ModuleCard";
import QuickActionsSidebar from "@/components/portal/QuickActionsSidebar";
import BottomTabNav from "@/components/portal/BottomTabNav";
import GridBackground from "@/components/primitives/GridBackground";
import SmithersPresencePanel from "@/components/portal/SmithersPresencePanel";
import QBandStatus from "@/components/portal/QBandStatus";
import AZSystem from "@/components/sections/AZSystem";
import ProjectCard from "@/components/portal/projects/ProjectCard";
import NewProjectModal from "@/components/portal/projects/NewProjectModal";
import StakeDrawer from "@/components/portal/stake/StakeDrawer";

export default function PortalPage() {
    const nav = useNavigate();
    const { user, loading: authLoading, openAuthModal } = useAuth();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastSync, setLastSync] = useState(null);
    const [activeProjects, setActiveProjects] = useState([]);
    const [stakeOpen, setStakeOpen] = useState(false);
    const [newProjectOpen, setNewProjectOpen] = useState(false);

    useEffect(() => {
        if (!authLoading && !user) openAuthModal("login");
    }, [authLoading, user, openAuthModal]);

    useEffect(() => {
        if (!user) return;
        let mounted = true;
        (async () => {
            const local = await readLocalState();
            if (local && mounted) {
                setState(local);
                setLoading(false);
            }
            try {
                const fresh = await apiGetPortalState();
                if (!mounted) return;
                setState(fresh);
                setLastSync(fresh.server_time);
                await writeLocalState(fresh);
            } catch (e) {
                if (!local && mounted) setLoading(false);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [user]);

    // Fetch top active projects for the strip
    useEffect(() => {
        if (!user) return;
        let mounted = true;
        (async () => {
            try {
                const { projects } = await apiProjectsList("active");
                if (mounted) setActiveProjects((projects || []).slice(0, 3));
            } catch (_e) { /* offline */ }
        })();
        return () => { mounted = false; };
    }, [user]);

    const ent = state?.user?.entitlements || user?.entitlements || {};
    const unlocked = useMemo(() => ent.unlocked_letters || [], [ent.unlocked_letters]);
    const completedModules = useMemo(
        () => Object.entries(state?.progress?.modules || {})
            .filter(([, v]) => v?.completed_at)
            .map(([k]) => k),
        [state?.progress?.modules],
    );
    const lastActive = state?.progress?.last_active_module;

    const activePhase = useMemo(
        () => getActivePhase(unlocked, lastActive, completedModules),
        [unlocked, lastActive, completedModules],
    );

    const onContinue = useCallback(() => {
        const letter = lastActive || activePhase.letters.find((L) => unlocked.includes(L)) || activePhase.letters[0];
        nav(`/portal/module/${letter}`);
    }, [lastActive, activePhase, unlocked, nav]);

    // userProgress shape for AZSystem dashboard variant
    const azUserProgress = useMemo(
        () => ({
            unlocked: new Set(unlocked),
            completed: new Set(completedModules),
            active: lastActive || null,
            genesisIZ: !!(ent.genesis_iz_early_access || ent.tier === "genesis"),
        }),
        [unlocked, completedModules, lastActive, ent.genesis_iz_early_access, ent.tier],
    );

    if (authLoading || (user && loading && !state)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-tca-deep text-tca-ink">
                <Loader2 size={28} className="text-tca-cyan animate-spin" />
            </div>
        );
    }

    if (!user) {
        return (
            <div data-testid="portal-locked" className="min-h-screen flex flex-col items-center justify-center bg-tca-deep text-tca-ink px-5 gap-4">
                <div className="font-mono-tight text-[10px] uppercase tracking-[0.36em] text-tca-cyan/80">
                    // Operator Portal — Sign In Required
                </div>
                <button onClick={() => openAuthModal("login")} className="font-mono-tight text-[11px] uppercase tracking-[0.22em] py-3 px-7 border border-tca-cyan text-tca-cyan-hi rounded-full hover:shadow-glow-cyan-xs">
                    Sign In
                </button>
            </div>
        );
    }

    const name = displayName(state?.user || user);
    const tier = ent.tier || "operator";
    const pathLabel = buildPathLabel({ user: state?.user || user, progress: state?.progress });
    const access = accessStateLabel(state?.user || user);

    return (
        <div data-testid="portal-page" className="relative min-h-screen bg-tca-deep text-tca-ink overflow-x-hidden pb-20 lg:pb-0">
            <GridBackground fine />
            {/* Ambient blueprint backdrop */}
            <div aria-hidden="true" className="pointer-events-none fixed inset-0" style={{
                background: "radial-gradient(circle at 75% -10%, rgba(0,219,255,0.08), transparent 60%)",
            }} />
            {/* Subtle floating particles in welcome header zone */}
            <div aria-hidden="true" className="pointer-events-none fixed top-0 left-0 right-0 h-[420px] overflow-hidden">
                {[...Array(14)].map((_, i) => (
                    <motion.span
                        key={i}
                        className="absolute w-0.5 h-0.5 rounded-full bg-tca-cyan/40"
                        animate={{ y: [0, -16, 0], opacity: [0.2, 0.7, 0.2] }}
                        transition={{ duration: 6 + i * 0.3, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
                        style={{ left: `${(i * 7) % 100}%`, top: `${20 + (i * 13) % 60}%`, boxShadow: "0 0 4px rgba(0,219,255,0.55)" }}
                    />
                ))}
            </div>

            <PortalNav />

            <main className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 py-8 sm:py-12">
                {/* ── Welcome header ───────────────────────────────── */}
                <motion.section
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-12"
                    data-testid="portal-hero"
                >
                    <div className="font-mono-tight text-[11px] uppercase tracking-[0.32em] text-tca-cyan/85" data-testid="welcome-eyebrow">
                        // welcome back
                    </div>
                    <h1
                        className="mt-3 font-display text-[40px] sm:text-[60px] leading-[1.0] tracking-[-0.025em]"
                        data-testid="welcome-name"
                    >
                        <span className="neon-cyan">{name}.</span>
                    </h1>

                    <div className="mt-5 flex flex-wrap gap-x-7 gap-y-2 font-mono-tight text-[10px] uppercase tracking-[0.28em]" data-testid="welcome-status-grid">
                        <span className="text-tca-ink-muted">
                            // tier: <span className="text-tca-cyan-hi ml-1">{tier.toUpperCase()}</span>
                        </span>
                        <span className="text-tca-ink-muted">
                            // build path: <span className="text-tca-cyan-hi ml-1">{pathLabel}</span>
                        </span>
                        <span className="text-tca-ink-muted">
                            // access: <span className="text-tca-cyan-hi ml-1">{access}</span>
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={onContinue}
                        data-testid="continue-building-btn"
                        className="
                            group mt-7 inline-flex items-center gap-3
                            font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs
                            py-3.5 px-7 border border-tca-cyan text-tca-cyan-hi
                            bg-gradient-to-b from-[rgba(0,219,255,0.22)] to-[rgba(0,219,255,0.06)]
                            hover:text-white shadow-glow-cyan-xs hover:shadow-glow-cyan-lg
                            rounded-full transition-all duration-300
                        "
                    >
                        Continue Building
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </button>
                </motion.section>

                {/* ── Two-column grid: main + right rail ───────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
                    <div>
                        {/* Current Build Progress — A-Z map embedded */}
                        <section data-testid="current-build-progress" className="mb-14">
                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/80 mb-5">
                                // current build progress
                            </div>
                            <AZSystem variant="dashboard" userProgress={azUserProgress} />
                        </section>

                        {/* Active Projects strip */}
                        <section data-testid="active-projects-strip" className="mb-14">
                            <div className="flex items-baseline justify-between gap-4 flex-wrap mb-5">
                                <div>
                                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/80">
                                        // active projects
                                    </div>
                                    <h2 className="mt-2 font-display text-2xl text-tca-ink">
                                        Your live builds
                                    </h2>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setNewProjectOpen(true)}
                                        data-testid="portal-new-project-btn"
                                        className="inline-flex items-center gap-1.5 font-mono-tight text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border border-tca-cyan/40 text-tca-cyan hover:text-tca-cyan-hi hover:border-tca-cyan transition-all"
                                    >
                                        <Plus size={11} /> New
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => nav("/portal/projects")}
                                        data-testid="portal-view-all-projects"
                                        className="inline-flex items-center gap-1.5 font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-ink-muted hover:text-tca-cyan transition-colors"
                                    >
                                        View all <ArrowRight size={11} />
                                    </button>
                                </div>
                            </div>
                            {activeProjects.length === 0 ? (
                                <div className="border border-dashed border-tca-cyan/20 bg-tca-bg-panel/35 p-8 text-center">
                                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/65">
                                        // no active builds
                                    </div>
                                    <p className="mt-2 text-sm text-tca-ink-muted">
                                        Initialize your first project — the ecosystem will track continuity.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setNewProjectOpen(true)}
                                        data-testid="portal-projects-empty-cta"
                                        className="mt-4 inline-flex items-center gap-2 font-mono-tight text-[10px] uppercase tracking-[0.22em] py-2 px-4 border border-tca-cyan/55 text-tca-cyan rounded-full hover:border-tca-cyan hover:text-tca-cyan-hi"
                                    >
                                        <Plus size={11} /> Initialize first project
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {activeProjects.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
                                </div>
                            )}
                        </section>

                        {/* Phase cards row */}
                        <section data-testid="phase-cards-row" className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                            {DASHBOARD_PHASES.map((p, i) => {
                                const status = phaseStatus(p, unlocked, completedModules);
                                const pct = phaseCompletionPct(p, completedModules);
                                return (
                                    <PhaseCard
                                        key={p.id}
                                        phase={p}
                                        status={p.id === activePhase.id ? "active" : status}
                                        completionPct={pct}
                                        index={i}
                                        onOpen={() => {
                                            const target = p.letters.find((L) => unlocked.includes(L)) || p.letters[0];
                                            nav(`/portal/module/${target}`);
                                        }}
                                    />
                                );
                            })}
                        </section>

                        {/* Active phase modules */}
                        <section data-testid="active-phase-modules">
                            <div className="flex items-baseline justify-between mb-5 gap-4 flex-wrap">
                                <div>
                                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/80">
                                        // active phase · {activePhase.name}
                                    </div>
                                    <h2 className="mt-2 font-display text-2xl text-tca-ink">
                                        {activePhase.letters.length} modules in this phase
                                    </h2>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {activePhase.letters.map((L, i) => {
                                    const c = CURRICULUM.find((x) => x.letter === L);
                                    const owned = unlocked.includes(L);
                                    const done = completedModules.includes(L);
                                    const status = done ? "complete" : owned ? (lastActive === L ? "in-progress" : "ready") : "dim";
                                    const fileCount = state?.file_counts?.[L] || 0;
                                    return (
                                        <PortalModuleCard
                                            key={L}
                                            letter={L}
                                            title={c?.title || L}
                                            summary={c?.summary || ""}
                                            status={status}
                                            fileCount={fileCount}
                                            index={i}
                                            onClick={() => owned && nav(`/portal/module/${L}`)}
                                        />
                                    );
                                })}
                            </div>
                        </section>

                        {/* Mobile-only Smithers + Q-Band — appears below content on small screens */}
                        <div className="lg:hidden mt-12 space-y-6">
                            <SmithersPresencePanel user={state?.user || user} progress={state?.progress} onStake={() => setStakeOpen(true)} />
                            <QBandStatus />
                        </div>
                    </div>

                    {/* ── Right rail ─────────────────────────────── */}
                    <aside className="hidden lg:flex flex-col gap-6 lg:sticky lg:top-24 self-start max-h-[calc(100vh-6rem)] overflow-y-auto pb-6">
                        <SmithersPresencePanel user={state?.user || user} progress={state?.progress} onStake={() => setStakeOpen(true)} />
                        <QBandStatus />
                        <QuickActionsSidebar
                            lastSyncedAt={lastSync}
                            activeLetters={unlocked.slice(0, 8)}
                            onSmithers={() => {}}
                            onStake={() => setStakeOpen(true)}
                        />
                    </aside>
                </div>
            </main>

            <BottomTabNav />

            {/* Drawers + modals */}
            <StakeDrawer open={stakeOpen} onClose={() => setStakeOpen(false)} />
            <NewProjectModal
                open={newProjectOpen}
                onOpenChange={setNewProjectOpen}
                onCreated={(p) => setActiveProjects((prev) => [p, ...prev].slice(0, 3))}
            />
        </div>
    );
}
